import axios from 'axios';
import { fromZonedTime } from 'date-fns-tz';
import { flushPendingSavesBeforeLogout } from './pendingSaveBeforeLogout';

// B.2: Verbose HTTP logs only when REACT_APP_DEBUG=true (never log token values).
const __API_VERBOSE_LOGS__ = process.env.REACT_APP_DEBUG === 'true';

function apiDebug(...args) {
  if (__API_VERBOSE_LOGS__) {
    console.log(...args);
  }
}

// Create axios instance with base URL
const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    apiDebug('API Request - Token:', token ? 'Present' : 'Missing');
    apiDebug('API Request - URL:', config.url);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      apiDebug('API Request - Authorization header set');
    } else {
      apiDebug('API Request - No token found in localStorage');
    }
    // Default Content-Type is application/json; that breaks multipart — browser must set
    // multipart/form-data with boundary so FastAPI can see File(...) fields.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (__API_VERBOSE_LOGS__) {
      console.log('API Response Error:', status, url, error.response?.data);
    }
    if (status === 401) {
      // Don't redirect if this was the login request (wrong password - already on login page)
      if (url.includes('/auth/login')) {
        return Promise.reject(error);
      }
      const hadToken = !!localStorage.getItem('access_token');
      apiDebug('Authentication error - hadToken:', hadToken, '- redirecting to login');
      return flushPendingSavesBeforeLogout()
        .catch(() => {})
        .then(() => {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(error);
        });
    }
    return Promise.reject(error);
  }
);

// Utility function to convert EST/EDT to UTC ISO string
function estToUtcISOString(estTimeString) {
  if (!estTimeString) {
    return estTimeString;
  }
  // DateTimePicker values are already absolute instants; do not re-interpret as Eastern wall clock.
  if (estTimeString instanceof Date) {
    return estTimeString.toISOString();
  }
  if (
    typeof estTimeString === 'string' &&
    /(?:Z|[+-]\d{2}:?\d{2})$/i.test(estTimeString)
  ) {
    return new Date(estTimeString).toISOString();
  }
  const estTimeZone = 'America/New_York';
  const utcDate = fromZonedTime(estTimeString, estTimeZone);
  return utcDate.toISOString();
}

// Authentication API calls
export const authAPI = {
  login: async (email, password) => {
    const params = new URLSearchParams();
    params.append('username', email); // OAuth2 expects 'username'
    params.append('password', password);

    const response = await api.post('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put('/auth/me/profile', profileData);
    return response.data;
  },

  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },
};

// Clients API calls
export const clientsAPI = {
  getClients: async (includeArchived = false) => {
    const response = await api.get('/clients', {
      params: { include_archived: includeArchived },
    });
    return response.data;
  },

  getClient: async (clientId) => {
    const response = await api.get(`/clients/${clientId}`);
    return response.data;
  },

  getClientReadiness: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/readiness`);
    return response.data;
  },

  archiveClient: async (clientId) => {
    const response = await api.patch(`/clients/${clientId}/archive`);
    return response.data;
  },

  unarchiveClient: async (clientId) => {
    const response = await api.patch(`/clients/${clientId}/unarchive`);
    return response.data;
  },

  createClient: async (clientData) => {
    const response = await api.post('/clients', clientData);
    return response.data;
  },

  updateClient: async (clientId, clientData) => {
    const response = await api.put(`/clients/${clientId}`, clientData);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}`);
    return response.data;
  },

  /** Task 4.5: client AI profile (brand voice, pillars, tone, hashtags, platform prefs). */
  getClientAiProfile: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/ai-profile`);
    return response.data;
  },

  updateClientAiProfile: async (clientId, body) => {
    const response = await api.put(`/clients/${clientId}/ai-profile`, body);
    return response.data;
  },

  /** Phase 15.2: suggestions from caption edits */
  getCaptionEditSuggestions: async (clientId, limit = 6) => {
    const response = await api.get(`/clients/${clientId}/caption-edit-suggestions`, {
      params: { limit },
    });
    return response.data;
  },

  /** Task 3.1: watermark template (logo + layout). */
  getWatermarkTemplate: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/watermark-template`);
    return response.data;
  },

  updateWatermarkTemplate: async (clientId, body) => {
    const response = await api.put(`/clients/${clientId}/watermark-template`, body);
    return response.data;
  },

  uploadWatermarkLogo: async (clientId, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(
      `/clients/${clientId}/watermark-template/logo`,
      form,
    );
    return response.data;
  },

  /** Task 4.6: caption QA test run logs (compare tone / profile iterations). */
  listCaptionQualityRuns: async (clientId, limit = 50) => {
    const response = await api.get(`/clients/${clientId}/caption-quality-runs`, {
      params: { limit },
    });
    return response.data;
  },

  createCaptionQualityRun: async (clientId, body) => {
    const response = await api.post(
      `/clients/${clientId}/caption-quality-runs`,
      body,
    );
    return response.data;
  },

  /** Queue Puppeteer public Instagram scrape → client_posts (source=public_scrape). */
  queuePublicProfileScrape: async (clientId, platformAccountId = null) => {
    const body =
      platformAccountId != null ? { platform_account_id: platformAccountId } : {};
    const response = await api.post(
      `/clients/${clientId}/scrape-public-profile`,
      body
    );
    return response.data;
  },

  /** Media library (Phase 1.3–1.4): list assets; optional filters for API-side filtering. */
  listClientMedia: async (clientId, params = {}) => {
    const response = await api.get(`/clients/${clientId}/media`, {
      params,
    });
    return response.data;
  },

  /** W4.F1: soft-delete — hide from Gallery; keep DB row for ML. */
  archiveClientMediaAsset: async (clientId, mediaAssetId) => {
    const response = await api.patch(
      `/clients/${clientId}/media/${mediaAssetId}/archive`,
    );
    return response.data;
  },

  /** W4.F1: restore archived Gallery asset. */
  unarchiveClientMediaAsset: async (clientId, mediaAssetId) => {
    const response = await api.patch(
      `/clients/${clientId}/media/${mediaAssetId}/unarchive`,
    );
    return response.data;
  },

  /**
   * W4.F1: permanent delete (files + row). Requires hard=true.
   * Prefer archiveClientMediaAsset for the default Gallery path.
   */
  hardDeleteClientMediaAsset: async (clientId, mediaAssetId) => {
    const response = await api.delete(
      `/clients/${clientId}/media/${mediaAssetId}`,
      { params: { hard: true } },
    );
    return response.data;
  },

  /** Per-asset caption steering (post intent, blog promo, vertical). */
  patchClientMediaAsset: async (clientId, mediaAssetId, body) => {
    const response = await api.patch(
      `/clients/${clientId}/media/${mediaAssetId}`,
      body,
    );
    return response.data;
  },

  /** W3.F3: per-client saved caption intent library (MRU). */
  listSavedCaptionIntents: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/saved-caption-intents`);
    return response.data;
  },

  createSavedCaptionIntent: async (clientId, { name, intent_text }) => {
    const response = await api.post(`/clients/${clientId}/saved-caption-intents`, {
      name,
      intent_text,
    });
    return response.data;
  },

  useSavedCaptionIntent: async (clientId, intentId) => {
    const response = await api.post(
      `/clients/${clientId}/saved-caption-intents/${intentId}/use`,
    );
    return response.data;
  },

  deleteSavedCaptionIntent: async (clientId, intentId) => {
    await api.delete(`/clients/${clientId}/saved-caption-intents/${intentId}`);
  },

  /** Public IG profile scrape rows (client_posts) for Gallery import (Phase 1.7). */
  listPublicScrapePosts: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/public-scrape-posts`);
    return response.data;
  },

  /** Import scraped IG posts into media_assets (source=scraped). */
  importPublicScrapeToLibrary: async (clientId, clientPostIds) => {
    const response = await api.post(
      `/clients/${clientId}/media/import-public-scrape`,
      { client_post_ids: clientPostIds },
    );
    return response.data;
  },

  /** Task 2.3: list resolved pillar prompts (YAML + DB overrides). */
  getClientPillarPrompts: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/pillar-prompts`);
    return response.data;
  },

  /** Task 2.3: save per-client pillar prompt override. */
  upsertClientPillarPrompt: async (clientId, body) => {
    const response = await api.put(`/clients/${clientId}/pillar-prompts`, body);
    return response.data;
  },

  /** Task 2.4: Gemini caption variants → caption_drafts. Optional ``brief_amendment`` query (Tier 1.6). */
  generateCaptionsForMediaAsset: async (clientId, mediaAssetId, params = {}) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/generate-captions`,
      null,
      { params },
    );
    return response.data;
  },

  /** Task 5.7: ML engagement score per caption variant (pre-approval). */
  scoreCaptionVariantsMl: async (clientId, mediaAssetId, body = {}) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/caption-variants/ml-scores`,
      body,
    );
    return response.data;
  },

  listCaptionDrafts: async (clientId, mediaAssetId) => {
    const response = await api.get(
      `/clients/${clientId}/media/${mediaAssetId}/caption-drafts`,
    );
    return response.data;
  },

  /** Task 2.5: edit variants + review_status; syncs approved_caption on the asset. */
  patchCaptionDraft: async (clientId, mediaAssetId, draftId, body) => {
    const response = await api.patch(
      `/clients/${clientId}/media/${mediaAssetId}/caption-drafts/${draftId}`,
      body,
    );
    return response.data;
  },

  /** Task 2.6: (re)generate Instagram + Facebook + LinkedIn hashtag sets per variant. */
  generateCaptionDraftHashtagStrategy: async (clientId, mediaAssetId, draftId) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/caption-drafts/${draftId}/generate-hashtag-strategy`,
    );
    return response.data;
  },

  /** Task 5.2: platform-aware caption + hashtag shaping (batch for Gallery preview). */
  captionFormatBatch: async (clientId, body) => {
    const response = await api.post(
      `/clients/${clientId}/caption-format-batch`,
      body,
    );
    return response.data;
  },

  /** Tier L2 (1.6): approve asset + copy + proposed time (does not schedule). */
  approveDraftPackage: async (clientId, mediaAssetId, body) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/approve-draft-package`,
      body,
    );
    return response.data;
  },

  /** Tier L2 (1.6): rejection + optional brief amendment for regeneration loop. */
  rejectDraftPackage: async (clientId, mediaAssetId, body) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/reject-draft-package`,
      body,
    );
    return response.data;
  },

  withdrawDraftPackage: async (clientId, mediaAssetId) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/withdraw-draft-package`,
    );
    return response.data;
  },

  /** Phase 2.1: ML-suggested slot preview (no side effects). */
  scheduleWithMlPreview: async (clientId, mediaAssetId, platform = 'instagram') => {
    const response = await api.get(
      `/clients/${clientId}/media/${mediaAssetId}/schedule-with-ml/preview`,
      { params: { platform } },
    );
    return response.data;
  },

  /** Phase 2.1: commit promote + schedule at chosen time. */
  scheduleWithMlCommit: async (clientId, mediaAssetId, body) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/schedule-with-ml/commit`,
      body,
    );
    return response.data;
  },

  /** Task 5.1 + 5.2: draft post(s) from approved caption; optional ``platform`` / ``platforms``. */
  promoteMediaAssetToPost: async (clientId, mediaAssetId, options = {}) => {
    const payload = {};
    if (Array.isArray(options.platforms) && options.platforms.length) {
      payload.platforms = options.platforms
        .map((p) => String(p || '').trim().toLowerCase())
        .filter(Boolean);
    } else if (options.platform && String(options.platform).trim()) {
      payload.platform = String(options.platform).trim().toLowerCase();
    }
    if (options.content_pillar && String(options.content_pillar).trim()) {
      payload.content_pillar = String(options.content_pillar).trim().toLowerCase();
    }
    if (options.variant_index !== undefined && options.variant_index !== null) {
      const n = Number(options.variant_index);
      if (Number.isFinite(n)) {
        payload.variant_index = Math.trunc(n);
      }
    }
    // Phase 16.R11: YouTube metadata overlay for promote/publish.
    if (options.youtube && typeof options.youtube === 'object') {
      payload.youtube = options.youtube;
    }
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/promote-to-post`,
      payload,
    );
    return response.data;
  },

  /** Task 2.7: Celery batch caption generation */
  startCaptionBatch: async (clientId, mediaAssetIds) => {
    const response = await api.post(`/clients/${clientId}/media/caption-batches`, {
      media_asset_ids: mediaAssetIds,
    });
    return response.data;
  },

  getCaptionBatch: async (clientId, batchId) => {
    const response = await api.get(
      `/clients/${clientId}/media/caption-batches/${batchId}`,
    );
    return response.data;
  },

  /** Task 5.4: image Pillow watermark or video preview-frame watermark per id. */
  batchWatermarkMedia: async (clientId, mediaAssetIds) => {
    const response = await api.post(`/clients/${clientId}/media/batch-watermark`, {
      media_asset_ids: mediaAssetIds,
    });
    return response.data;
  },

  /** Extract JPEG posters for video Gallery cards (no watermark). */
  ensureVideoThumbnails: async (clientId, mediaAssetIds) => {
    const response = await api.post(
      `/clients/${clientId}/media/ensure-video-thumbnails`,
      { media_asset_ids: mediaAssetIds },
    );
    return response.data;
  },

  /** Task 5.4: approve variant 0 on newest ready draft per asset. */
  batchApproveCaptions: async (clientId, mediaAssetIds) => {
    const response = await api.post(
      `/clients/${clientId}/media/batch-approve-captions`,
      { media_asset_ids: mediaAssetIds },
    );
    return response.data;
  },

  /** Task 5.4: promote + staggered suggested calendar slots. */
  batchScheduleToCalendar: async (clientId, body) => {
    const response = await api.post(
      `/clients/${clientId}/media/batch-schedule-to-calendar`,
      body,
    );
    return response.data;
  },

  uploadClientMedia: async (
    clientId,
    file,
    { source = 'upload', content_pillar, rights_confirmed = false } = {},
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('source', source);
    if (content_pillar) form.append('content_pillar', content_pillar);
    if (rights_confirmed) form.append('rights_confirmed', 'true');
    const response = await api.post(`/clients/${clientId}/media`, form);
    return response.data;
  },

  /** Task 3.2 / 3.5: composite logo onto library image (Pillow). */
  applyWatermarkMedia: async (clientId, mediaAssetId) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/watermark`,
    );
    return response.data;
  },

  /** Task 3.3: watermarked frame preview for video. */
  applyWatermarkVideoThumbnail: async (clientId, mediaAssetId) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/watermark-video-thumbnail`,
    );
    return response.data;
  },

  /** Task 3.3: full video with logo overlay (may be slow). */
  applyWatermarkVideoFull: async (clientId, mediaAssetId) => {
    const response = await api.post(
      `/clients/${clientId}/media/${mediaAssetId}/watermark-video-full`,
    );
    return response.data;
  },
};

/** Public base URL for static `/uploads` (strip `/api/v1` from API base). */
export function getPublicUploadBaseUrl() {
  const base =
    process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  return base.replace(/\/api\/v1\/?$/, '');
}

export function publicFileUrl(fileUrl) {
  if (!fileUrl) return '';
  if (fileUrl.startsWith('http')) return fileUrl;
  const root = getPublicUploadBaseUrl();
  const path = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
  return `${root}${path}`;
}

// Platform Accounts API calls
export const platformAccountsAPI = {
  getPlatformAccounts: async (clientId) => {
    // Optionally filter by clientId
    const params = clientId ? { client_id: clientId } : {};
    const response = await api.get('/platform-accounts', { params });
    return response.data;
  },

  getPlatformAccount: async (accountId) => {
    const response = await api.get(`/platform-accounts/${accountId}`);
    return response.data;
  },

  getYouTubePlaylists: async (clientId) => {
    const response = await api.get(
      `/platform-accounts/${clientId}/youtube/playlists`
    );
    return response.data;
  },

  createPlatformAccount: async (data) => {
    const response = await api.post('/platform-accounts', data);
    return response.data;
  },

  updatePlatformAccount: async (accountId, data) => {
    const response = await api.put(`/platform-accounts/${accountId}`, data);
    return response.data;
  },

  deletePlatformAccount: async (accountId) => {
    const response = await api.delete(`/platform-accounts/${accountId}`);
    return response.data;
  },
};

// Posts API calls
export const postsAPI = {
  getPosts: async (filters = {}) => {
    // Only include filters with non-empty values
    const params = {};
    if (filters.client_id) params.client_id = filters.client_id;
    if (filters.platform) params.platform = filters.platform;
    if (filters.status) params.status = filters.status;
    const response = await api.get('/posts', { params });
    return response.data;
  },

  getPost: async (postId) => {
    const response = await api.get(`/posts/${postId}`);
    return response.data;
  },

  /** W4.F3: theme A/B/C export (JSON or CSV) keyed by platform post_id. */
  exportThemeLog: async (clientId, format = 'json') => {
    const response = await api.get('/posts/theme-export', {
      params: { client_id: clientId, format },
      ...(format === 'csv' ? { responseType: 'blob' } : {}),
    });
    return response.data;
  },

  createPost: async (postData) => {
    // Convert scheduled_time if present
    let dataToSend = { ...postData };
    if (dataToSend.scheduled_time) {
      dataToSend.scheduled_time = estToUtcISOString(dataToSend.scheduled_time);
    }
    const response = await api.post('/posts', dataToSend);
    return response.data;
  },

  updatePost: async (postId, postData) => {
    const response = await api.put(`/posts/${postId}`, postData);
    return response.data;
  },

  deletePost: async (postId) => {
    const response = await api.delete(`/posts/${postId}`);
    return response.data;
  },

  schedulePost: async (postId, scheduledTime) => {
    // Convert to UTC ISO string
    const utcISOString = estToUtcISOString(scheduledTime);
    const response = await api.post(`/posts/${postId}/schedule`, {
      scheduled_time: utcISOString,
    });
    return response.data;
  },

  publishPost: async (postId) => {
    const response = await api.post(`/posts/${postId}/publish`);
    return response.data;
  },

  getClientPosts: async (clientId, filters = {}) => {
    const response = await api.get(`/posts/client/${clientId}`, {
      params: filters,
    });
    return response.data;
  },

  createAndSchedulePost: async (postData) => {
    // Convert scheduled_time if present
    let dataToSend = { ...postData };
    if (dataToSend.scheduled_time) {
      dataToSend.scheduled_time = estToUtcISOString(dataToSend.scheduled_time);
    }
    const response = await api.post('/posts/schedule', dataToSend);
    return response.data;
  },
};

// Scheduling API calls
export const schedulingAPI = {
  getCalendarEvents: async (startDate, endDate, filters = {}) => {
    const response = await api.get('/scheduling/calendar', {
      params: { start_date: startDate, end_date: endDate, ...filters },
    });
    return response.data;
  },

  /** Task 5.3: next slot from top-performer analysis (or peak heuristic). */
  getSuggestedPostingSlot: async ({ client_id, platform, content_type }) => {
    const params = {
      client_id,
      platform: platform || 'instagram',
    };
    if (content_type) params.content_type = content_type;
    const response = await api.get('/scheduling/suggested-slot', { params });
    return response.data;
  },

  // AI Scheduling Recommendations
  getAISchedulingRecommendations: async (
    clientId,
    contentPlan,
    platform = 'facebook',
    timezone = 'America/New_York'
  ) => {
    const response = await api.post('/scheduling/ai-recommendations', {
      client_id: clientId,
      content_plan: contentPlan,
      platform: platform,
      timezone: timezone,
    });
    return response.data;
  },

  optimizeExistingSchedule: async (clientId) => {
    const response = await api.post('/scheduling/ai-optimize-schedule', {
      client_id: clientId,
    });
    return response.data;
  },

  bulkSchedulePosts: async (postIds, scheduledTime, timezone = 'UTC') => {
    // Convert to UTC ISO string
    const utcISOString = estToUtcISOString(scheduledTime);
    const response = await api.post('/scheduling/bulk-schedule', {
      post_ids: postIds,
      scheduled_time: utcISOString,
      timezone,
    });
    return response.data;
  },

  createRecurringSchedule: async (templatePost, scheduleConfig) => {
    const response = await api.post('/scheduling/recurring-schedule', {
      template_post: templatePost,
      schedule_config: scheduleConfig,
    });
    return response.data;
  },

  checkScheduleConflicts: async (startDate, endDate, filters = {}) => {
    const response = await api.get('/scheduling/conflicts', {
      params: { start_date: startDate, end_date: endDate, ...filters },
    });
    return response.data;
  },

  getUpcomingPosts: async (days = 7, filters = {}) => {
    const response = await api.get('/scheduling/upcoming', {
      params: { days, ...filters },
    });
    return response.data;
  },

  reschedulePost: async (postId, newScheduledTime) => {
    // Convert to UTC ISO string
    const utcISOString = estToUtcISOString(newScheduledTime);
    const response = await api.post(
      `/scheduling/reschedule/${postId}?new_scheduled_time=${encodeURIComponent(
        utcISOString
      )}`
    );
    return response.data;
  },

  pauseSchedule: async (postIds) => {
    const response = await api.post('/scheduling/pause-schedule', postIds);
    return response.data;
  },

  getSchedulingStats: async (clientId = null) => {
    const response = await api.get('/scheduling/stats', {
      params: clientId ? { client_id: clientId } : {},
    });
    return response.data;
  },
};

// Dashboard stats (single source of truth for main dashboard cards)
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats', { timeout: 15000 });
    return response.data;
  },
  /** Disk use for uploads volume — banner + notifications in Layout */
  getStorageStatus: async () => {
    const response = await api.get('/dashboard/storage-status', {
      timeout: 10000,
    });
    return response.data;
  },
};

// Analytics API calls
// Phase 22.3: review-send log. Records requests INITIATED, never delivery
// confirmations — the tool hands off to the OS messaging app and loses visibility.
// customer_hash is intentionally not returned by this endpoint.
export const reputationAPI = {
  getReviewSends: async ({ clientId = null, limit = 50, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (clientId != null) params.client_id = clientId;
    const response = await api.get('/review-sends', { params });
    return response.data;
  },
  // Two independent weekly counts, side by side. Never a ratio — see
  // services/review_correlation.py for why.
  getReviewCorrelation: async ({ clientId, weeks = 12 }) => {
    const response = await api.get('/reputation/review-correlation', {
      params: { client_id: clientId, weeks },
    });
    return response.data;
  },
};

export const analyticsAPI = {
  getOverview: async (clientId = null, timeRange = '30d') => {
    const params = { time_range: timeRange };
    if (clientId) params.client_id = clientId;
    const response = await api.get('/analytics/overview', { params });
    return response.data;
  },

  getPlatformBreakdown: async (clientId = null, timeRange = '30d') => {
    const params = { time_range: timeRange };
    if (clientId) params.client_id = clientId;
    const response = await api.get('/analytics/platform-breakdown', { params });
    return response.data;
  },

  getEngagementTimeline: async (clientId = null, timeRange = '30d') => {
    const params = { time_range: timeRange };
    if (clientId) params.client_id = clientId;
    const response = await api.get('/analytics/engagement-timeline', {
      params,
    });
    return response.data;
  },

  getTopPosts: async (clientId = null, timeRange = '30d', limit = 10) => {
    const params = { time_range: timeRange, limit };
    if (clientId) params.client_id = clientId;
    const response = await api.get('/analytics/top-posts', { params });
    return response.data;
  },

  /** Phase 16.3: queue engagement re-fetch for a client's full published history. */
  backfillEngagement: async (clientId) => {
    const response = await api.post('/analytics/backfill-engagement', null, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  getClientPerformance: async (timeRange = '30d') => {
    const response = await api.get('/analytics/client-performance', {
      params: { time_range: timeRange },
    });
    return response.data;
  },

  /** Task 5.6: caption variant + pillar prompt source vs engagement */
  getCaptionTemplatePerformance: async ({
    clientId = null,
    timeRange = '90d',
  } = {}) => {
    const params = { time_range: timeRange };
    if (clientId) params.client_id = clientId;
    const response = await api.get('/analytics/caption-template-performance', {
      params,
    });
    return response.data;
  },

  getGscEngagementCorrelation: async ({ clientId, limit = 10 } = {}) => {
    const response = await api.get('/analytics/gsc-engagement-correlation', {
      params: { client_id: clientId, limit },
    });
    return response.data;
  },

  /**
   * Compose dashboard analytics (overview + charts) for the Analytics page.
   *
   * A.3 — Not platform-reported KPIs (replace in Phase C with Insights / real rates):
   * - total_reach_estimated: round(total_engagement * 12). Heuristic only, not impressions.
   * - avg_engagement_rate_approx: min(100, (total_engagement / total_posts) * 0.1), not reach/follower-based.
   */
  getAnalytics: async (timeRange = '7d', clientId = null) => {
    const [overview, reachTotals, platform, timeline, topPosts] = await Promise.all([
      analyticsAPI.getOverview(clientId, timeRange),
      api.get('/analytics/reach-impressions', {
        params: {
          time_range: timeRange,
          ...(clientId ? { client_id: clientId } : {}),
        },
      }).then((r) => r.data),
      analyticsAPI.getPlatformBreakdown(clientId, timeRange),
      analyticsAPI.getEngagementTimeline(clientId, timeRange),
      analyticsAPI.getTopPosts(clientId, timeRange, 10),
    ]);
    const avgRateApprox =
      overview.total_posts > 0
        ? Math.min(
            100,
            Number(
              (
                (overview.total_engagement / overview.total_posts) *
                0.1
              ).toFixed(1),
            ),
          )
        : 0;
    return {
      data: {
        total_posts: overview.total_posts,
        total_engagement: overview.total_engagement,
        // C.2: Real totals from stored platform metrics (omit nulls; no heuristics).
        total_reach: reachTotals?.total_reach_or_impressions ?? 0,
        total_reach_exact: reachTotals?.total_reach ?? 0,
        total_impressions: reachTotals?.total_impressions ?? 0,
        avg_engagement_rate_approx: avgRateApprox,
        platform_distribution: platform,
        engagement_over_time: timeline,
        top_posts: topPosts,
      },
    };
  },
};

// Chatbot API calls
export const chatbotAPI = {
  sendMessage: async (
    message,
    context = null,
    conversationHistory = null,
    clientId = null
  ) => {
    const response = await api.post('/chatbot/chat', {
      message,
      context,
      conversation_history: conversationHistory,
      client_id: clientId, // Add client_id for context-aware responses
    });
    return response.data;
  },

  /** Upload image; LLM returns title, caption, hashtags. Optional message (e.g. "write a caption"). Pass clientId + mediaAssetId to persist Gemini asset_analysis on that library row (Task 2.2). */
  generateCaptionFromMedia: async (
    file,
    message = '',
    { clientId, mediaAssetId, contentPillar } = {},
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (message && message.trim()) formData.append('message', message.trim());
    if (clientId != null && clientId !== '')
      formData.append('client_id', String(clientId));
    if (mediaAssetId != null && mediaAssetId !== '')
      formData.append('media_asset_id', String(mediaAssetId));
    if (contentPillar != null && contentPillar !== '')
      formData.append('content_pillar', String(contentPillar));
    const response = await api.post(
      '/chatbot/generate-caption-from-media',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },
};

// AI Content Generation API calls
export const aiContentAPI = {
  generatePosts: async (requestData) => {
    const response = await api.post('/ai-content/generate-posts', requestData);
    return response.data;
  },

  generateHashtags: async (requestData) => {
    const response = await api.post(
      '/ai-content/generate-hashtags',
      requestData
    );
    return response.data;
  },

  optimizeContent: async (requestData) => {
    const response = await api.post(
      '/ai-content/optimize-content',
      requestData
    );
    return response.data;
  },

  /** Task 5.2: Optimize post draft - returns predicted_score + suggestions */
  optimizePost: async (requestData) => {
    const response = await api.post('/ai-content/optimize-post', requestData);
    return response.data;
  },

  /** Task 5.4: Record a creator applying one optimize-post suggestion */
  applyOptimizePostSuggestion: async (requestData) => {
    const response = await api.post('/ai-content/optimize-post/apply', requestData);
    return response.data;
  },

  /** Task 5.3: Lightweight score for draft - returns predicted_score + signals */
  scoreDraft: async (requestData) => {
    const response = await api.post('/ai-content/score-draft', requestData);
    return response.data;
  },

  generateTemplates: async (requestData) => {
    const response = await api.post(
      '/ai-content/generate-templates',
      requestData
    );
    return response.data;
  },

  getPlatformGuidelines: async (platform) => {
    const response = await api.get(
      `/ai-content/platform-guidelines/${platform}`
    );
    return response.data;
  },

  getContentTypes: async () => {
    const response = await api.get('/ai-content/content-types');
    return response.data;
  },

  bulkGenerateContent: async (requestData) => {
    const response = await api.post('/ai-content/bulk-generate', requestData);
    return response.data;
  },
};

// Media API calls
export const mediaAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    // Get auth token
    const token = localStorage.getItem('access_token');

    // Use fetch directly to avoid axios Content-Type issues
    const response = await fetch(`${API_BASE_URL}/media/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type - let browser set it for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }

    return await response.json();
  },
};

// Facebook Pages API
export async function fetchFacebookPages(clientId = null) {
  const token = localStorage.getItem('access_token');
  const url = clientId
    ? `${API_BASE_URL}/auth/facebook/pages?client_id=${clientId}`
    : `${API_BASE_URL}/auth/facebook/pages`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch Facebook Pages');
  return (await res.json()).pages;
}

export async function refreshFacebookPagesFromApi() {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE_URL}/auth/facebook/refresh-pages`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  return data;
}

export async function verifyFacebookToken(clientId) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE_URL}/clients/${clientId}/facebook-token-status`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to verify token');
  return res.json();
}

/** Link Instagram to the client's assigned Facebook Page (use if dual-publish to IG didn't work) */
export async function syncInstagramFromPage(clientId) {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE_URL}/clients/${clientId}/sync-instagram-from-page`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.detail || err.message || res.statusText || 'Failed to sync Instagram';
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return res.json();
}

export async function unassignFacebookPageFromClient(clientId) {
  const userToken = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE_URL}/clients/${clientId}/facebook-page`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${userToken}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.detail || err.message || res.statusText || 'Failed to unassign Facebook page';
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return res.json();
}

export async function assignFacebookPageToClient(clientId, page) {
  const userToken = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE_URL}/clients/${clientId}/facebook-page`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    },
    body: JSON.stringify(page),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.detail || err.message || res.statusText || 'Failed to assign Facebook Page';
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return await res.json();
}

// LinkedIn API calls
export const linkedinAPI = {
  getLinkedInAuthUrl: async (clientId) => {
    const response = await api.get('/auth/linkedin/login', {
      params: { client_id: clientId },
    });
    return response.data.auth_url;
  },
  exchangeLinkedInCode: async (code, state) => {
    const response = await api.get('/auth/linkedin/callback', {
      params: { code, state },
    });
    return response.data;
  },
  getLinkedInStatus: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/linkedin-account`);
    return response.data;
  },
  disconnectLinkedIn: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}/linkedin-account`);
    return response.data;
  },
};

// Facebook API calls
export const facebookAPI = {
  getFacebookAuthUrl: async () => {
    const response = await api.get('/auth/facebook/login');
    return response.data.auth_url;
  },
  exchangeFacebookCode: async (code, state) => {
    const response = await api.get('/auth/facebook/callback', {
      params: { code, state },
    });
    return response.data;
  },
  getFacebookStatus: async () => {
    const response = await api.get('/auth/facebook/status');
    return response.data;
  },
  disconnectFacebook: async () => {
    const response = await api.post('/auth/facebook/disconnect');
    return response.data;
  },
  getAvailablePages: async () => {
    const response = await api.get('/auth/facebook/pages');
    return response.data;
  },
};

// Instagram: posting uses Facebook Login + Page → Instagram Business (no separate Instagram OAuth).
export const instagramAPI = {
  getInstagramStatus: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/instagram-account`);
    return response.data;
  },
  disconnectInstagram: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}/instagram-account`);
    return response.data;
  },
  // Manual Instagram connection (username + optional password for browser posting)
  connectInstagramManually: async (clientId, username, password = null) => {
    const response = await api.post('/auth/instagram/manual-connect', {
      client_id: clientId,
      instagram_username: username,
      instagram_account_id: `manual_${username}`,
      access_token: `manual_token_${username}`,
      ...(password ? { instagram_password: password } : {}),
    });
    return response.data;
  },
};

// X API calls (Puppeteer-based: connect via browser login, post via Puppeteer)
export const xAPI = {
  getXPuppeteerStartUrl: async (clientId) => {
    const response = await api.get('/auth/x/puppeteer-start', {
      params: { client_id: clientId, return_url: 1 },
    });
    return response.data.url;
  },
  getXAuthUrl: async (clientId) => {
    const response = await api.get('/auth/x/login', {
      params: { client_id: clientId },
    });
    return response.data.auth_url;
  },
  exchangeXCode: async (code, clientId) => {
    const response = await api.get('/auth/x/callback', {
      params: { code, state: JSON.stringify({ client_id: clientId }) },
    });
    return response.data;
  },
  getXStatus: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/x-account`);
    return response.data;
  },
  disconnectX: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}/x-account`);
    return response.data;
  },
};

// Pinterest API calls (Puppeteer-based: connect via browser login, post via Puppeteer)
export const pinterestAPI = {
  getPinterestPuppeteerStartUrl: async (clientId) => {
    const response = await api.get('/auth/pinterest/puppeteer-start', {
      params: { client_id: clientId, return_url: 1 },
    });
    return response.data.url;
  },
  getPinterestStatus: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/pinterest-account`);
    return response.data;
  },
  disconnectPinterest: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}/pinterest-account`);
    return response.data;
  },
  getPinterestBoards: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/pinterest-boards`);
    return response.data.boards || [];
  },
};

// YouTube API calls
export const youtubeAPI = {
  getYouTubeAuthUrl: async (clientId) => {
    const response = await api.get('/auth/youtube/login', {
      params: { client_id: clientId },
    });
    return response.data.auth_url;
  },
  exchangeYouTubeCode: async (code, clientId) => {
    const response = await api.get('/auth/youtube/callback', {
      params: { code, state: JSON.stringify({ client_id: clientId }) },
    });
    return response.data;
  },
  getYouTubeStatus: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/youtube-account`);
    return response.data;
  },
  disconnectYouTube: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}/youtube-account`);
    return response.data;
  },
};

// Tumblr API calls
export const tumblrAPI = {
  getTumblrAuthUrl: async (clientId) => {
    const response = await api.get('/auth/tumblr/login', {
      params: { client_id: clientId },
    });
    return response.data.auth_url;
  },
  exchangeTumblrCode: async (code, clientId) => {
    const response = await api.get('/auth/tumblr/callback', {
      params: { code, state: JSON.stringify({ client_id: clientId }) },
    });
    return response.data;
  },
  getTumblrStatus: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/tumblr-account`);
    return response.data;
  },
  disconnectTumblr: async (clientId) => {
    const response = await api.delete(`/clients/${clientId}/tumblr-account`);
    return response.data;
  },
};

// Google Search Console (per-client OAuth + snapshots)
export const gscAPI = {
  getLoginUrl: async (clientId, preferredSiteUrl = null) => {
    const params = { client_id: clientId };
    if (preferredSiteUrl) params.preferred_site_url = preferredSiteUrl;
    const response = await api.get('/auth/gsc/login', { params });
    return response.data.auth_url;
  },
  getSummary: async (clientId) => {
    const response = await api.get(`/clients/${clientId}/gsc/summary`);
    return response.data;
  },
  disconnect: async (clientId) => {
    const response = await api.delete('/auth/gsc/disconnect', {
      params: { client_id: clientId },
    });
    return response.data;
  },
  selectProperty: async (clientId, siteUrl) => {
    const response = await api.post('/auth/gsc/select-property', {
      client_id: clientId,
      site_url: siteUrl,
    });
    return response.data;
  },
  syncNow: async (clientId) => {
    const response = await api.post(`/clients/${clientId}/gsc/sync`);
    return response.data;
  },
};

// Google Business Profile reviews (Phase 22.6). Consent is per client and never
// auto-connects: the callback returns a list of listings and one has to be picked.
export const gbpAPI = {
  getLoginUrl: async (clientId) => {
    const response = await api.get('/auth/gbp/login', {
      params: { client_id: clientId },
    });
    return response.data.auth_url;
  },
  getConnection: async (clientId) => {
    const response = await api.get('/gbp/connection', {
      params: { client_id: clientId },
    });
    return response.data;
  },
  // Re-lists the pending consent's locations — the popup's list is gone after a reload.
  getPendingLocations: async (clientId) => {
    const response = await api.get('/auth/gbp/pending-locations', {
      params: { client_id: clientId },
    });
    return response.data;
  },
  selectLocation: async (clientId, locationName) => {
    const response = await api.post('/auth/gbp/select-location', {
      client_id: clientId,
      location_name: locationName,
    });
    return response.data;
  },
  disconnect: async (clientId) => {
    const response = await api.delete('/auth/gbp/disconnect', {
      params: { client_id: clientId },
    });
    return response.data;
  },
  syncNow: async (clientId) => {
    const response = await api.post('/gbp/sync', null, {
      params: { client_id: clientId },
    });
    return response.data;
  },
};

// Scheduled Posts API
export const scheduledPostsAPI = {
  create: async (data) => {
    const response = await api.post('/posts/scheduled', data);
    return response.data;
  },
  list: async () => {
    const response = await api.get('/posts/scheduled');
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/posts/scheduled/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/posts/scheduled/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/posts/scheduled/${id}`);
    return response.data;
  },
};

// Campaigns API
export const campaignsAPI = {
  getCampaigns: async () => {
    const response = await api.get('/campaigns');
    return response.data;
  },

  getCampaign: async (campaignId) => {
    const response = await api.get(`/campaigns/${campaignId}`);
    return response.data;
  },

  createCampaign: async (campaignData) => {
    const response = await api.post('/campaigns', campaignData);
    return response.data;
  },

  updateCampaign: async (campaignId, campaignData) => {
    const response = await api.put(`/campaigns/${campaignId}`, campaignData);
    return response.data;
  },

  deleteCampaign: async (campaignId) => {
    const response = await api.delete(`/campaigns/${campaignId}`);
    return response.data;
  },

  activateCampaign: async (campaignId) => {
    const response = await api.post(`/campaigns/${campaignId}/activate`);
    return response.data;
  },

  pauseCampaign: async (campaignId) => {
    const response = await api.post(`/campaigns/${campaignId}/pause`);
    return response.data;
  },

  getCampaignPerformance: async (campaignId) => {
    const response = await api.get(`/campaigns/${campaignId}/performance`);
    return response.data;
  },

  // AI Campaign Optimization
  getAICampaignOptimization: async (
    clientId,
    campaignType = 'engagement',
    targetAudience = null,
    budget = null,
    durationDays = null
  ) => {
    const response = await api.post('/campaigns/ai-optimization', {
      client_id: clientId,
      campaign_type: campaignType,
      target_audience: targetAudience,
      budget: budget,
      duration_days: durationDays,
    });
    return response.data;
  },

  createAIAutomatedCampaign: async (
    clientId,
    campaignType = 'engagement',
    targetAudience = null,
    budget = null,
    durationDays = null
  ) => {
    const response = await api.post('/campaigns/ai-create-automated', {
      client_id: clientId,
      campaign_type: campaignType,
      target_audience: targetAudience,
      budget: budget,
      duration_days: durationDays,
    });
    return response.data;
  },

  optimizeExistingCampaign: async (campaignId) => {
    const response = await api.post(`/campaigns/${campaignId}/ai-optimize`);
    return response.data;
  },
};

// AI Agents API
export const aiAgentsAPI = {
  startAutomatedCampaign: async (clientId, campaignType = 'engagement') => {
    const response = await api.post('/ai-agents/start-automated-campaign', null, {
      params: { client_id: clientId, campaign_type: campaignType },
    });
    return response.data;
  },

  getCampaignStatus: async (clientId) => {
    const response = await api.get(`/ai-agents/campaign-status/${clientId}`);
    return response.data;
  },

  stopAutomatedCampaign: async (clientId) => {
    const response = await api.post(
      `/ai-agents/stop-automated-campaign/${clientId}`
    );
    return response.data;
  },

  generateContent: async (clientId) => {
    const response = await api.post('/ai-agents/generate-content', null, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  optimizeCampaign: async (clientId) => {
    const response = await api.post('/ai-agents/optimize-campaign', null, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  getCampaignTypes: async () => {
    const response = await api.get('/ai-agents/campaign-types');
    return response.data;
  },

  startConversation: async (clientId) => {
    const response = await api.post('/ai-agents/conversation/start', null, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  answerQuestion: async (clientId, answer) => {
    const response = await api.post(
      `/ai-agents/conversation/answer/${clientId}`,
      { answer }
    );
    return response.data;
  },

  getConversationStatus: async (clientId) => {
    const response = await api.get(
      `/ai-agents/conversation/status/${clientId}`
    );
    return response.data;
  },

  skipQuestion: async (clientId) => {
    const response = await api.post(
      `/ai-agents/conversation/skip/${clientId}`
    );
    return response.data;
  },

  getConversationQuestions: async () => {
    const response = await api.get('/ai-agents/conversation/questions');
    return response.data;
  },
};

// Token Status API
export const tokenStatusAPI = {
  checkAllTokens: async () => {
    const response = await api.get('/token-status/check-all');
    return response.data;
  },
  getExpiringTokens: async (hoursAhead = 24) => {
    const response = await api.get('/token-status/expiring', {
      params: { hours_ahead: hoursAhead },
    });
    return response.data;
  },
  checkPlatformToken: async (platform) => {
    const response = await api.get(`/token-status/check/${platform}`);
    return response.data;
  },
  removeAllInvalidTokens: async () => {
    const response = await api.delete('/token-status/remove-invalid');
    return response.data;
  },
  removeSingleToken: async (accountId) => {
    const response = await api.delete(`/token-status/remove/${accountId}`);
    return response.data;
  },
};

// Competitor Analysis API
export const competitorsAPI = {
  getCompetitors: async (filters = {}) => {
    apiDebug('API: Getting competitors with filters:', filters);
    const response = await api.get('/competitors/', {
      params: filters,
    });
    apiDebug('API: Response received:', response.data);
    return response.data;
  },

  getCompetitor: async (competitorId) => {
    const response = await api.get(`/competitors/${competitorId}`);
    return response.data;
  },

  createCompetitor: async (data) => {
    const response = await api.post('/competitors', data);
    return response.data;
  },

  updateCompetitor: async (competitorId, data) => {
    const response = await api.put(`/competitors/${competitorId}`, data);
    return response.data;
  },

  deleteCompetitor: async (competitorId) => {
    const response = await api.delete(`/competitors/${competitorId}`);
    return response.data;
  },

  getCompetitorAnalytics: async (competitorId, filters = {}) => {
    const response = await api.get(`/competitors/${competitorId}/analytics`, {
      params: filters,
    });
    return response.data;
  },

  getCompetitorPosts: async (competitorId, filters = {}) => {
    const response = await api.get(`/competitors/${competitorId}/posts`, {
      params: filters,
    });
    return response.data;
  },

  getCompetitorInsights: async (competitorId, filters = {}) => {
    const response = await api.get(`/competitors/${competitorId}/insights`, {
      params: filters,
    });
    return response.data;
  },

  discoverForClient: async (clientId) => {
    apiDebug('API: Discovering competitors for client:', clientId);
    // Phase 17.1: FastAPI discovery only — N8N webhook path removed.
    const response = await api.post('/competitors/discover-for-client', {
      client_id: clientId,
      max_competitors: 10,
    });
    return response.data;
  },

  getCompetitorSummary: async (competitorId) => {
    const response = await api.get(`/competitors/${competitorId}/summary`);
    return response.data;
  },

  getCompetitorTrends: async (competitorId, filters = {}) => {
    const response = await api.get(`/competitors/${competitorId}/trends`, {
      params: filters,
    });
    return response.data;
  },

  getCompetitorHashtags: async (competitorId, filters = {}) => {
    const response = await api.get(`/competitors/${competitorId}/hashtags`, {
      params: filters,
    });
    return response.data;
  },

  getCompetitorContentThemes: async (competitorId, filters = {}) => {
    const response = await api.get(
      `/competitors/${competitorId}/content-themes`,
      { params: filters }
    );
    return response.data;
  },

  getCompetitorPostingPatterns: async (competitorId, filters = {}) => {
    const response = await api.get(
      `/competitors/${competitorId}/posting-patterns`,
      { params: filters }
    );
    return response.data;
  },

  // ML Analysis functions
  getMarketShare: async (competitorId) => {
    const response = await api.get(`/ml/market-share/${competitorId}`);
    return response.data;
  },

  getQualityScore: async (competitorId) => {
    const response = await api.get(`/ml/quality-score/${competitorId}`);
    return response.data;
  },

  getSentiment: async (competitorId) => {
    const response = await api.get(`/ml/sentiment/${competitorId}`);
    return response.data;
  },

  getCompetitiveInsights: async (competitorId) => {
    const response = await api.get(`/ml/competitive-insights/${competitorId}`);
    return response.data;
  },

  triggerScrape: async (competitorId, platform = null) => {
    const params = platform ? { platform } : {};
    const response = await api.post(
      `/competitors/${competitorId}/scrape`,
      null,
      { params }
    );
    return response.data;
  },

  getScrapingStatus: async (competitorId) => {
    const response = await api.get(
      `/competitors/${competitorId}/scraping-status`
    );
    return response.data;
  },
};

// ML Analysis API - Client-level aggregation + engagement prediction
export const mlAnalysisAPI = {
  // Task 3.4: Recommendations (top-performer / heuristics / LLM)
  getRecommendations: async ({
    client_id = null,
    competitor_id = null,
    platform = null,
    limit = 100,
    use_llm_fallback = false,
  } = {}) => {
    const params = {};
    if (client_id != null) params.client_id = client_id;
    if (competitor_id != null) params.competitor_id = competitor_id;
    if (platform != null) params.platform = platform;
    if (limit != null) params.limit = limit;
    if (use_llm_fallback != null) params.use_llm_fallback = use_llm_fallback;
    const response = await api.get('/ml/recommendations', { params });
    return response.data;
  },

  // Task 2.6: Predict engagement for draft content
  predictEngagement: async ({
    content = '',
    platform = 'instagram',
    competitor_id,
    scheduled_time,
    content_type = 'text',
  }) => {
    const body = {
      content,
      platform: platform.toLowerCase(),
      competitor_id,
      content_type: (content_type || 'text').toLowerCase(),
    };
    if (scheduled_time) {
      body.scheduled_time =
        scheduled_time instanceof Date
          ? scheduled_time.toISOString()
          : scheduled_time;
    }
    const response = await api.post('/ml/predict-engagement', body);
    return response.data;
  },

  // Client-level endpoints
  getClientSummary: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/summary`);
    return response.data;
  },

  getClientCompetitiveOverview: async (clientId) => {
    const response = await api.get(
      `/ml/client/${clientId}/competitive-overview`
    );
    return response.data;
  },

  getClientContentAnalysis: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/content-analysis`);
    return response.data;
  },

  getClientSentimentAnalysis: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/sentiment-analysis`);
    return response.data;
  },

  getClientPlatformPresence: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/platform-presence`);
    return response.data;
  },

  getClientRecommendations: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/recommendations`);
    return response.data;
  },

  /** Phase 0.3: Abstract competitor patterns (hooks, formats, length, cadence) — no raw captions. */
  getCompetitorPatternBrief: async (clientId, { days = 90 } = {}) => {
    const response = await api.get(
      `/ml/client/${clientId}/competitor-pattern-brief`,
      { params: { days } },
    );
    return response.data;
  },

  getClientFullReport: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/report`);
    return response.data;
  },

  /** Task 5.4: Model reporting - accuracy, attribution, trends */
  getModelReport: async (days = 90) => {
    const response = await api.get('/ml/model-report', { params: { days } });
    return response.data;
  },

  exportClientReportPDF: async (clientId) => {
    try {
      const response = await api.get(`/ml/client/${clientId}/report/pdf`, {
        responseType: 'blob',
      });
      const blob = response.data;
      // FastAPI error bodies still arrive as blobs when responseType=blob.
      if (
        blob &&
        typeof blob.type === 'string' &&
        blob.type.includes('application/json')
      ) {
        const text = await blob.text();
        let detail = 'PDF export failed';
        try {
          const parsed = JSON.parse(text);
          detail =
            typeof parsed.detail === 'string'
              ? parsed.detail
              : JSON.stringify(parsed.detail || parsed);
        } catch {
          detail = text || detail;
        }
        throw new Error(detail);
      }
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `report_${clientId}_${new Date().getTime()}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true, filename };
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          const detail =
            typeof parsed.detail === 'string'
              ? parsed.detail
              : JSON.stringify(parsed.detail || parsed);
          const err = new Error(detail);
          err.response = { data: { detail } };
          throw err;
        } catch (inner) {
          if (inner?.response?.data?.detail) throw inner;
        }
      }
      throw error;
    }
  },

  exportClientReportHTML: async (clientId, download = true) => {
    try {
      const response = await api.get(`/ml/client/${clientId}/report/html`, {
        responseType: 'blob',
      });
      const blob = response.data;
      // FastAPI error bodies still arrive as blobs when responseType=blob (W4.BUG-3).
      if (
        blob &&
        typeof blob.type === 'string' &&
        blob.type.includes('application/json')
      ) {
        const text = await blob.text();
        let detail = 'HTML export failed';
        try {
          const parsed = JSON.parse(text);
          detail =
            typeof parsed.detail === 'string'
              ? parsed.detail
              : JSON.stringify(parsed.detail || parsed);
        } catch {
          detail = text || detail;
        }
        throw new Error(detail);
      }

      const htmlContent = await blob.text();

      if (download) {
        const url = window.URL.createObjectURL(
          new Blob([htmlContent], { type: 'text/html' })
        );
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `report_${clientId}_${new Date().getTime()}.html`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return { success: true, filename, content: htmlContent };
      }

      return { success: true, content: htmlContent };
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          const detail =
            typeof parsed.detail === 'string'
              ? parsed.detail
              : JSON.stringify(parsed.detail || parsed);
          const err = new Error(detail);
          err.response = { data: { detail } };
          throw err;
        } catch (inner) {
          if (inner?.response?.data?.detail) throw inner;
        }
      }
      throw error;
    }
  },

  getClientContentBreakdown: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/content-breakdown`);
    return response.data;
  },

  getClientContentInsights: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/content-insights`);
    return response.data;
  },

  triggerContentBreakdownBackfill: async (clientId) => {
    const response = await api.post(
      `/ml/client/${clientId}/backfill-content-breakdown`
    );
    return response.data;
  },

  getClientSEOAnalysis: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/seo-analysis`);
    return response.data;
  },

  triggerSEOAnalysisBackfill: async (clientId) => {
    const response = await api.post(
      `/ml/client/${clientId}/backfill-seo-analysis`
    );
    return response.data;
  },

  getClientGapAnalysis: async (clientId) => {
    const response = await api.get(`/ml/client/${clientId}/gap-analysis`);
    return response.data;
  },

  // Task 4.2: A/B Evaluation (model monitoring)
  getABEvaluation: async () => {
    const response = await api.get('/ml/ab-evaluation');
    return response.data;
  },
  triggerABBackfill: async () => {
    const response = await api.post('/ml/ab-evaluation/backfill');
    return response.data;
  },

  // Phase 15.1 / 19.4: Weekly model learnings — optional days window (7–365)
  getWeeklyLearnings: async (days = 7) => {
    const params = days !== 7 ? { days } : {};
    const response = await api.get('/ml/weekly-learnings', { params });
    return response.data;
  },

  getCaptionEditLearningStats: async () => {
    const response = await api.get('/ml/caption-edit-learning');
    return response.data;
  },
  recomputeWeeklyLearnings: async (notify = false) => {
    const response = await api.post('/ml/weekly-learnings/recompute', null, {
      params: { notify },
    });
    return response.data;
  },

  // Task 4.4: Drift Alerts
  getDriftAlerts: async () => {
    const response = await api.get('/ml/drift-alerts');
    return response.data;
  },

  getDriftSchemaAnnotations: async () => {
    const response = await api.get('/ml/drift-schema-annotations');
    return response.data;
  },

  resetDriftBaseline: async (triggerRetrain = true) => {
    const response = await api.post('/ml/drift-baseline/reset', null, {
      params: { trigger_retrain: triggerRetrain },
    });
    return response.data;
  },

  // Competitor-level endpoints (for drill-down)
  getCompetitorMarketShare: async (competitorId) => {
    const response = await api.get(`/ml/market-share/${competitorId}`);
    return response.data;
  },

  getCompetitorQualityScore: async (competitorId) => {
    const response = await api.get(`/ml/quality-score/${competitorId}`);
    return response.data;
  },

  getCompetitorSentiment: async (competitorId) => {
    const response = await api.get(`/ml/sentiment/${competitorId}`);
    return response.data;
  },

  getCompetitorCompetitiveInsights: async (competitorId) => {
    const response = await api.get(`/ml/competitive-insights/${competitorId}`);
    return response.data;
  },
};

// Task Monitoring API
export const tasksAPI = {
  getTaskStatus: async (taskId) => {
    const response = await api.get(`/tasks/task/${taskId}`);
    return response.data;
  },

  getActiveTasks: async () => {
    const response = await api.get('/tasks/active');
    return response.data;
  },

  getScheduledTasks: async () => {
    const response = await api.get('/tasks/scheduled');
    return response.data;
  },

  getTaskStats: async () => {
    const response = await api.get('/tasks/stats');
    return response.data;
  },

  getScrapingStatus: async () => {
    const response = await api.get('/tasks/scraping/status');
    return response.data;
  },

  getRecentScrapingActivity: async (limit = 20) => {
    const response = await api.get('/tasks/scraping/recent-activity', {
      params: { limit },
    });
    return response.data;
  },

  getScrapingSchedule: async () => {
    const response = await api.get('/tasks/scraping/schedule');
    return response.data;
  },
};

// Ranking Tracking API calls
export const rankingTrackingAPI = {
  // Keyword Management
  createKeyword: async (clientId, keywordData) => {
    const response = await api.post(
      `/ranking/clients/${clientId}/keywords`,
      keywordData
    );
    return response.data;
  },

  getKeywords: async (clientId, isActive = null) => {
    const params = isActive !== null ? { is_active: isActive } : {};
    const response = await api.get(`/ranking/clients/${clientId}/keywords`, {
      params,
    });
    return response.data;
  },

  getSuggestedKeywords: async (clientId, maxSuggestions = 15) => {
    const response = await api.get(
      `/ranking/clients/${clientId}/suggested-keywords`,
      {
        params: { max_suggestions: maxSuggestions },
      }
    );
    return response.data;
  },

  updateKeyword: async (keywordId, keywordData) => {
    const response = await api.put(
      `/ranking/keywords/${keywordId}`,
      keywordData
    );
    return response.data;
  },

  deleteKeyword: async (keywordId) => {
    const response = await api.delete(`/ranking/keywords/${keywordId}`);
    return response.data;
  },

  // Ranking Data
  getClientRankings: async (clientId, days = 30) => {
    const response = await api.get(`/ranking/clients/${clientId}/rankings`, {
      params: { days },
    });
    return response.data;
  },

  getKeywordRankings: async (keywordId, days = 365) => {
    const response = await api.get(`/ranking/keywords/${keywordId}/rankings`, {
      params: { days },
    });
    return response.data;
  },

  // Manual Checks
  checkKeywordRanking: async (keywordId) => {
    const response = await api.post(`/ranking/keywords/${keywordId}/check`);
    return response.data;
  },

  checkAllRankings: async (clientId) => {
    const response = await api.post(
      `/ranking/clients/${clientId}/check-rankings`
    );
    return response.data;
  },

  /** Phase 13.2: CSE configured vs mock/estimated rankings */
  getCseStatus: async () => {
    const response = await api.get('/ranking/cse-status');
    return response.data;
  },

  // Google Blocks
  getGoogleBlocks: async (isResolved = null, limit = 50) => {
    const params = {};
    if (isResolved !== null) params.is_resolved = isResolved;
    params.limit = limit;
    const response = await api.get('/ranking/google-blocks', { params });
    return response.data;
  },
};

// Facebook Page → Groups sharing
export const groupSharingAPI = {
  getFacebookPagesForClient: async (clientId) => {
    const response = await api.get('/group-sharing/facebook-pages-for-client', {
      params: { client_id: clientId },
    });
    return response.data;
  },
  getConfig: async (platformAccountId) => {
    const response = await api.get('/group-sharing/config', {
      params: { platform_account_id: platformAccountId },
    });
    return response.data;
  },
  getHistory: async (platformAccountId, limit = 20) => {
    const response = await api.get('/group-sharing/history', {
      params: { platform_account_id: platformAccountId, limit },
    });
    return response.data;
  },
  updateConfig: async (platformAccountId, groupIds, autoShare, defaultText = '') => {
    const response = await api.put('/group-sharing/config', {
      platform_account_id: platformAccountId,
      group_ids: groupIds,
      auto_share: autoShare,
      default_text: defaultText || null,
    });
    return response.data;
  },
  discover: async (platformAccountId) => {
    // Long timeout: Puppeteer may wait for user to complete CAPTCHA (up to 2 min) before returning groups.
    const response = await api.post(
      '/group-sharing/discover',
      { platform_account_id: platformAccountId },
      { timeout: 300000 }
    );
    return response.data;
  },
  sharePost: async (postId, platformAccountId = null) => {
    const response = await api.post('/group-sharing/share-post', {
      post_id: postId,
      platform_account_id: platformAccountId ?? undefined,
    });
    return response.data;
  },
  /** Test share: most recent post (or optional post_url) for this Page → first configured group only. Long timeout for Puppeteer. */
  testShare: async (platformAccountId, postUrl = null) => {
    const body = { platform_account_id: platformAccountId };
    if (postUrl && typeof postUrl === 'string' && postUrl.trim()) body.post_url = postUrl.trim();
    const response = await api.post('/group-sharing/test-share', body, { timeout: 600000 });
    return response.data;
  },
  /** Test share to 2 groups: same flow but shares to first 2 configured groups (post → group 1, then post → group 2). */
  testShare2: async (platformAccountId, postUrl = null) => {
    const body = { platform_account_id: platformAccountId };
    if (postUrl && typeof postUrl === 'string' && postUrl.trim()) body.post_url = postUrl.trim();
    const response = await api.post('/group-sharing/test-share-2', body, { timeout: 600000 });
    return response.data;
  },
  /** Share most recent post (or post_url) to ALL configured groups. */
  testShareAll: async (platformAccountId, postUrl = null) => {
    const body = { platform_account_id: platformAccountId };
    if (postUrl && typeof postUrl === 'string' && postUrl.trim()) body.post_url = postUrl.trim();
    const response = await api.post('/group-sharing/test-share-all', body, { timeout: 900000 });
    return response.data;
  },
};

// In-app notification bell (mirrors Slack ops alerts)
export const notificationsAPI = {
  list: async ({ unreadOnly = false, limit = 100 } = {}) => {
    const response = await api.get('/notifications', {
      params: { unread_only: unreadOnly, limit },
    });
    return response.data;
  },
  create: async (payload) => {
    const response = await api.post('/notifications', payload);
    return response.data;
  },
  markRead: async (id) => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },
  clearAll: async () => {
    const response = await api.delete('/notifications');
    return response.data;
  },
};

export default api;
